import { useCallback, useEffect, useMemo, useState } from "react"
import Modal from "react-bootstrap/Modal"
import Form from "react-bootstrap/Form"
import Button from "react-bootstrap/Button"
import Row from "react-bootstrap/Row"
import Col from "react-bootstrap/Col"
import { toast } from "react-toastify"
import { getVehicleSettingsForArea, setVehicleSettingsForArea } from "../utils/vehicleStorage"

const electricFieldDefinitions = [
  { name: "vehicleModel", label: "Araç modeli" },
  { name: "vehicleWeight", label: "Araç Ağırlığı" },
  { name: "batteryCapacity", label: "Batarya Kapasitesi" },
  { name: "range", label: "Menzil" },
  { name: "dcMaxCharge", label: "DC Maksimum şarj" },
  { name: "acMaxCharge", label: "AC Maksimum şarj" },
  { name: "maxLoadCapacity", label: "Maksimum yük kapasitesi" },
  { name: "chargeType", label: "Şarj Tipi" },
]

const buildDefaultElectricValues = () =>
  electricFieldDefinitions.reduce((acc, def) => {
    acc[def.name] = ""
    return acc
  }, {})

const defaultElectricValues = buildDefaultElectricValues()

const createEmptyElectricValues = () => ({ ...defaultElectricValues })

const vehiclePresets = [
  {
    key: "fordTransitTrend",
    label: "Ford E-Transit (Trend / 350 L3H2)",
    values: {
      vehicleModel: "Ford E-Transit (Trend / 350 L3H2)",
      vehicleWeight: "3500",
      batteryCapacity: "68",
      range: "200",
      dcMaxCharge: "115",
      acMaxCharge: "11",
      maxLoadCapacity: "1611",
      chargeType: "DC 115 kW / AC 11 kW",
    },
  },
  {
    key: "renaultKangooMedium",
    label: "Renault Kangoo E-Tech Van (Medium / 45 kWh)",
    values: {
      vehicleModel: "Renault Kangoo E-Tech Van (Medium / 45 kWh)",
      vehicleWeight: "2300",
      batteryCapacity: "45",
      range: "220",
      dcMaxCharge: "80",
      acMaxCharge: "22",
      maxLoadCapacity: "800",
      chargeType: "DC 80 kW / AC 22 kW",
    },
  },
  {
    key: "fordTransitCustom",
    label: "Ford E-Transit Custom (Medium LCV, 2024 model)",
    values: {
      vehicleModel: "Ford E-Transit Custom (Medium LCV, 2024 model)",
      vehicleWeight: "2800",
      batteryCapacity: "64",
      range: "270",
      dcMaxCharge: "125",
      acMaxCharge: "11",
      maxLoadCapacity: "1100",
      chargeType: "DC 125 kW / AC 11 kW",
    },
  },
]

const VehicleTypeModal = ({ state, onClose }) => {
  const [selectedType, setSelectedType] = useState("")
  const [electricValues, setElectricValues] = useState(() => createEmptyElectricValues())
  const [isSaving, setIsSaving] = useState(false)
  const [selectedPresetKey, setSelectedPresetKey] = useState(null)
  const isPresetLocked = Boolean(selectedPresetKey)

  const areaIdKey = useMemo(() => {
    if (!state?.areaId && state?.areaId !== 0) return null
    return String(state.areaId)
  }, [state?.areaId])

  const resetForm = useCallback(() => {
    setSelectedType("")
    setElectricValues(createEmptyElectricValues())
    setIsSaving(false)
    setSelectedPresetKey(null)
  }, [])

  const populateFromStorage = useCallback(() => {
    if (!state.show || !areaIdKey) return
    const stored = getVehicleSettingsForArea(areaIdKey)
    if (!stored) {
      resetForm()
      return
    }
    setSelectedType(stored.type || "")
    setSelectedPresetKey(stored.presetKey || null)
    if (stored.type === "Elektrikli" && stored.electric) {
      setElectricValues({
        ...createEmptyElectricValues(),
        ...stored.electric,
      })
    } else {
      setElectricValues(createEmptyElectricValues())
    }
  }, [areaIdKey, resetForm, state.show])

  useEffect(() => {
    if (state.show) {
      populateFromStorage()
    } else {
      resetForm()
    }
  }, [populateFromStorage, resetForm, state.show])

  const handleSelectVehicleType = (type) => {
    setSelectedType(type)
    if (type !== "Elektrikli") {
      setSelectedPresetKey(null)
    }
  }

  const handlePresetSelect = (preset) => {
    if (!preset) return
    setSelectedPresetKey(preset.key)
    if (selectedType !== "Elektrikli") {
      setSelectedType("Elektrikli")
    }
    setElectricValues({
      ...createEmptyElectricValues(),
      ...preset.values,
    })
  }

  const handleElectricValueChange = (event) => {
    const { name, value } = event.target
    setElectricValues((prev) => ({ ...prev, [name]: value }))
  }

  const handleModelSelectChange = (event) => {
    const value = event.target.value
    if (value === "custom") {
      setSelectedPresetKey(null)
      return
    }
    const preset = vehiclePresets.find((p) => p.key === value)
    handlePresetSelect(preset)
  }

  const handleSave = async () => {
    if (!areaIdKey || !selectedType) return
    setIsSaving(true)
    try {
      setVehicleSettingsForArea(areaIdKey, {
        type: selectedType,
        electric: selectedType === "Elektrikli" ? electricValues : undefined,
        presetKey: selectedPresetKey || undefined,
      })
      toast.success("Araç bilgileri kaydedildi")
      onClose()
    } catch (error) {
      toast.error("Araç bilgileri kaydedilemedi")
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Modal show={state.show} size="lg" onHide={isSaving ? () => {} : onClose}>
      <Modal.Header closeButton>
        <Modal.Title>{state.areaName || "Bölge"} Araç Tipi</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Araç Tipi</Form.Label>
            <div className="d-flex gap-3">
              <Form.Check
                type="radio"
                id={`vehicle-type-electric-${areaIdKey || "new"}`}
                label="Elektrikli"
                name="vehicleType"
                checked={selectedType === "Elektrikli"}
                onChange={() => handleSelectVehicleType("Elektrikli")}
              />
              <Form.Check
                type="radio"
                id={`vehicle-type-ice-${areaIdKey || "new"}`}
                label="İçten yanmalı"
                name="vehicleType"
                checked={selectedType === "İçten yanmalı"}
                onChange={() => handleSelectVehicleType("İçten yanmalı")}
              />
            </div>
          </Form.Group>
          {selectedType === "Elektrikli" && (
            <Row className="g-3">
              {electricFieldDefinitions.map((field) => (
                <Col xs={12} md={6} key={field.name}>
                  <Form.Group controlId={`electric-${field.name}`}>
                    <Form.Label>{field.label}</Form.Label>
                    {field.name === "vehicleModel" ? (
                      <>
                        <Form.Select value={selectedPresetKey || "custom"} onChange={handleModelSelectChange}>
                          <option value="custom">Araç modeli seç</option>
                          {vehiclePresets.map((preset) => (
                            <option value={preset.key} key={preset.key}>
                              {preset.label}
                            </option>
                          ))}
                        </Form.Select>
                      </>
                    ) : (
                      <Form.Control
                        type={field.name === "range" ? "number" : "text"}
                        inputMode={field.name === "range" ? "decimal" : undefined}
                        min={field.name === "range" ? "0" : undefined}
                        step={field.name === "range" ? "any" : undefined}
                        name={field.name}
                        value={electricValues[field.name]}
                        onChange={handleElectricValueChange}
                        placeholder={field.label}
                        disabled={isPresetLocked}
                      />
                    )}
                  </Form.Group>
                </Col>
              ))}
            </Row>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={isSaving}>
          İptal
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={!selectedType || isSaving}>
          {isSaving ? "Kaydediliyor..." : "Kaydet"}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default VehicleTypeModal
